import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { MessageSquare, Star, Send } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reviews = () => {
  const [heroRef, heroVisible] = useScrollAnimation(0.1);
  const [formRef, formVisible] = useScrollAnimation(0.2);
  const [reviewsListRef, reviewsListVisible] = useScrollAnimation(0.2);
  
  const [reviews, setReviews] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    rating: 5,
    review: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Fetch reviews from API
  // API is the only dependency as axios is a stable import and
  // response/err are local variables within the callback
  const fetchReviews = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reviews`);
      setReviews(response.data);
    } catch (err) {
      console.error('Error fetching reviews:', err);
    }
  }, [API]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await axios.post(`${API}/reviews`, formData);
      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        rating: 5,
        review: ''
      });
      // Refresh reviews
      fetchReviews();
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429) {
        setError('Too many requests. Please wait a minute and try again.');
      } else {
        setError(typeof detail === 'string' ? detail : 'Failed to submit review. Please check your details and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <Star 
        key={`star-${index}`}
        className={`w-5 h-5 ${index < rating ? 'fill-gold text-gold' : 'text-gray-300'}`}
      />
    ));
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Hero Section */}
      <section 
        ref={heroRef}
        className="relative h-96 flex items-center justify-center text-white overflow-hidden"
        style={{
          backgroundImage: "url('https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/tqhnalfr_IMG_0101.jpeg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 hero-bg-zoom" style={{backgroundImage: "url('https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/tqhnalfr_IMG_0101.jpeg')", backgroundSize: 'cover', backgroundPosition: 'center'}}></div>
        <div className="absolute inset-0 image-overlay-luxury"></div>
        <div className={`relative z-10 text-center ${heroVisible ? 'animate-fade-in-up' : 'opacity-0'}`} data-testid="reviews-hero">
          <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gold" />
          <h1 className="text-6xl md:text-7xl font-bold text-shadow-luxury" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>Guest Reviews</h1>
          <div className="w-24 h-1 bg-gold mx-auto mt-4"></div>
          <p className="text-2xl mt-6 italic">Share Your Experience With Us</p>
        </div>
      </section>

      {/* Submit Review Section */}
      <section ref={formRef} className="py-20 px-4 bg-cream-dark">
        <div className={`max-w-3xl mx-auto ${formVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <div className="bg-white p-8 md:p-12 rounded-lg shadow-luxury">
            <h2 className="text-3xl font-bold text-brown mb-8 text-center" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>Leave a Review</h2>

            {success && (
              <div className="mb-8 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                <h3 className="font-bold text-lg mb-2">Thank You! 🎉</h3>
                <p>Your review has been submitted successfully. We appreciate your feedback!</p>
              </div>
            )}

            {error && (
              <div className="mb-8 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Name */}
              <div className="mb-6">
                <label className="block text-gray-700 font-semibold mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                  placeholder="John Doe"
                />
              </div>

              {/* Email */}
              <div className="mb-6">
                <label className="block text-gray-700 font-semibold mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                  placeholder="john@example.com"
                />
              </div>

              {/* Rating */}
              <div className="mb-6">
                <label className="block text-gray-700 font-semibold mb-2">
                  Rating *
                </label>
                <select
                  name="rating"
                  value={formData.rating}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                >
                  <option value="5">⭐⭐⭐⭐⭐ (5 stars)</option>
                  <option value="4">⭐⭐⭐⭐ (4 stars)</option>
                  <option value="3">⭐⭐⭐ (3 stars)</option>
                  <option value="2">⭐⭐ (2 stars)</option>
                  <option value="1">⭐ (1 star)</option>
                </select>
              </div>

              {/* Review */}
              <div className="mb-8">
                <label className="block text-gray-700 font-semibold mb-2">
                  Your Review *
                </label>
                <textarea
                  name="review"
                  value={formData.review}
                  onChange={handleInputChange}
                  required
                  rows="6"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                  placeholder="Tell us about your experience at Mama-Mam's Kitchen..."
                ></textarea>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gold hover:bg-brown text-white font-bold py-4 px-8 rounded-lg text-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transform hover:scale-105 flex items-center justify-center gap-2"
              >
                <Send className="w-5 h-5" />
                {loading ? 'Submitting...' : 'Submit Review'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Display Reviews */}
      <section ref={reviewsListRef} className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className={`text-center mb-16 ${reviewsListVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <div className="w-16 h-1 bg-gold mx-auto mb-6"></div>
            <h2 className="text-5xl font-bold text-brown mb-4" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>What Our Guests Say</h2>
            <p className="text-lg text-gray-600 italic">Real reviews from real people</p>
          </div>

          {reviews.length === 0 ? (
            <div className={`text-center text-gray-500 py-12 ${reviewsListVisible ? 'animate-fade-in-up delay-200' : 'opacity-0'}`}>
              <p className="text-xl">No reviews yet. Be the first to share your experience!</p>
            </div>
          ) : (
            <div className={`grid md:grid-cols-2 gap-8 ${reviewsListVisible ? 'animate-fade-in-up delay-200' : 'opacity-0'}`}>
              {reviews.map((review) => (
                <div 
                  key={review.id}
                  className="bg-cream-dark p-8 rounded-lg shadow-luxury-hover border-l-4 border-gold"
                >
                  <div className="flex items-center gap-1 mb-4">
                    {renderStars(review.rating)}
                  </div>
                  <p className="text-gray-700 italic leading-relaxed mb-4">
                    "{review.review}"
                  </p>
                  <div className="border-t border-gold pt-4">
                    <p className="font-semibold text-brown">— {review.name}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {new Date(review.created_at).toLocaleDateString('en-GB', { 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric' 
                      })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Reviews;
